import React, { useState, useEffect, useMemo } from 'react';
import { Col, Container, Row, Card, CardHeader, CardBody, Input, Button } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import TableContainer from '../../../Components/Common/TableContainer';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import api,{handleApiError} from '../../../helpers/api';
import 'swiper/css';
import 'swiper/css/navigation';
import axios from 'axios';
import moment from 'moment';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";

SwiperCore.use([Navigation]);

// Custom CSS with table centering and reset button styling
const customStyles = `
  .log-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #f9fafb;
    width: 100%;
  }
  .log-table {
    width: 100% !important;
    margin: 0 auto !important;
    table-layout: auto;
  }
  .log-table th, .log-table td {
    vertical-align: middle;
    padding: 0.75rem;
    font-size: 0.9rem;
    color: #2a4966;
  }
  .log-table tr:hover {
    background-color: #f1f5f7;
  }
  .log-table .badge {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
  }
  .table-responsive {
    width: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: center;
  }
  .table-card {
    width: 100%;
    margin: 0;
    padding: 0;
  }
  .filter-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
    flex-wrap: nowrap;
    overflow-x: auto;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }
  .filter-container .form-select {
    border-radius: 4px;
    border: 1px solid #ced4da;
    min-width: 150px;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }
  .filter-container .form-select:focus {
    border-color: #0ab39c;
    box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
  }
  .filter-container .reset-btn {
    border-radius: 4px;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
    background-color: #6f42c1;
    color: #ffffff;
    border: none;
  }
  .filter-container .reset-btn:hover {
    background-color: #5a32a3;
  }
  .filter-container .reset-btn:focus {
    border-color: #0ab39c;
    box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
  }
  .card-header {
    color: #ffffff;
    border-bottom: none;
    padding: 1.5rem;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  .card-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: #ffffff;
  }
  .timeline-content {
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    background-color: #f9fafb;
    transition: transform 0.3s ease;
  }
  .timeline-content:hover {
    transform: translateY(-5px);
  }
  .timeline-content .badge {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
    border-radius: 4px;
  }
  .swiper-slide {
    padding: 0.5rem;
  }
  .badge-soft-purple {
    background-color: #6f42c1;
    color: #ffffff;
  }
  @media (max-width: 768px) {
    .filter-container {
      flex-wrap: nowrap;
      overflow-x: auto;
    }
    .filter-container .form-select, .filter-container .reset-btn {
      min-width: 100px;
      font-size: 0.85rem;
      padding: 0.4rem 0.8rem;
    }
    .log-table th, .log-table td {
      font-size: 0.8rem;
      padding: 0.5rem;
    }
    .log-table .badge {
      font-size: 0.7rem;
      padding: 0.3rem 0.6rem;
    }
    .card-body {
      padding: 1rem;
    }
    .card-header {
      padding: 1rem;
    }
    .card-title {
      font-size: 1.2rem;
    }
    .swiper-slide {
      padding: 0.3rem;
    }
    .timeline-content {
      padding: 0.5rem;
    }
    .timeline-content p, .timeline-content span {
      font-size: 0.85rem;
    }
  }
  @media (max-width: 576px) {
    .filter-container .form-select, .filter-container .reset-btn {
      min-width: 90px;
      font-size: 0.75rem;
    }
    .log-table th, .log-table td {
      font-size: 0.75rem;
      padding: 0.4rem;
    }
    .log-table .badge {
      font-size: 0.65rem;
      padding: 0.25rem 0.5rem;
    }
  }
`;

const Timeline = () => {
    const { t, i18n } = useTranslation();
    document.title = t('timeline.title');

    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [actionFilter, setActionFilter] = useState('all');
    const [resourceFilter, setResourceFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // Set moment locale based on i18next language
    useEffect(() => {
        moment.locale(i18n.language);
    }, [i18n.language]);

    useEffect(() => {
        fetchLogs();
    }, []);

    useEffect(() => {
        filterLogs();
    }, [actionFilter, resourceFilter, userFilter, dateFilter, logs]);

    const fetchLogs = async () => {
        try {
            const response = await api.get('/logs', {
                withCredentials: true,
            });
            const sortedLogs = response.data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            setLogs(sortedLogs);
            setFilteredLogs(sortedLogs);
        } catch (error) {
            console.error(t('timeline.error_fetch'), error);
            await handleApiError(error, dispatch, navigate);
        }
    };

    const filterLogs = () => {
        let filtered = [...logs];

        if (actionFilter !== 'all') {
            filtered = filtered.filter(log => {
                if (actionFilter === 'create') return log.action.includes('created');
                if (actionFilter === 'login') return log.action.includes('logged');
                return true;
            });
        }

        if (resourceFilter !== 'all') {
            filtered = filtered.filter(log => {
                if (resourceFilter === 'user') return log.action.includes('user');
                if (resourceFilter === 'formation') return log.action.includes('formation');
                if (resourceFilter === 'meeting') return log.action.includes('meeting');
                if (resourceFilter === 'carriere') return log.action.includes('carriere') || log.action.includes('candidature');
                if (resourceFilter === 'faq') return log.action.includes('FAQ');
                if (resourceFilter === 'ressource') return log.action.includes('ressource');
                return true;
            });
        }

        if (userFilter !== 'all') {
            filtered = filtered.filter(log => log.userId?._id === userFilter);
        }

        if (dateFilter !== 'all') {
            const now = moment();
            filtered = filtered.filter(log => {
                const logDate = moment(log.timestamp);
                if (dateFilter === 'today') return logDate.isSame(now, 'day');
                if (dateFilter === 'week') return logDate.isSame(now, 'week');
                if (dateFilter === 'month') return logDate.isSame(now, 'month');
                return true;
            });
        }

        setFilteredLogs(filtered);
    };

    const resetFilters = () => {
        setActionFilter('all');
        setResourceFilter('all');
        setUserFilter('all');
        setDateFilter('all');
    };

    const getActionColor = (action) => {
        if (action.includes('created')) return 'badge-soft-success';
        if (action.includes('updated')) return 'badge-soft-primary';
        if (action.includes('deleted')) return 'badge-soft-danger';
        if (action.includes('logged')) return 'badge-soft-purple';
        if (action.includes('failed')) return 'badge-soft-warning';
        return 'badge-soft-secondary';
    };

    const uniqueUsers = [...new Set(logs.map(log => log.userId?._id))].map(id =>
        logs.find(log => log.userId?._id === id)?.userId
    ).filter(user => user);

    // Table columns for TableContainer
    const columns = useMemo(
        () => [
            {
                Header: t('timeline.table.timestamp'),
                accessor: 'timestamp',
                filterable: true,
                Cell: ({ value }) => <span>{moment(value).format('YYYY-MM-DD HH:mm')}</span>,
            },
            {
                Header: t('timeline.table.user'),
                accessor: 'userId',
                filterable: true,
                Cell: ({ value }) => <span>{value?.firstname + ' ' + value?.lastname || t('timeline.unknown')}</span>,
            },
            {
                Header: t('timeline.table.role'),
                accessor: 'role',
                filterable: true,
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: t('timeline.table.action'),
                accessor: 'action',
                filterable: true,
                Cell: ({ value }) => (
                    <span className={`badge ${getActionColor(value)} text-uppercase`}>{value}</span>
                ),
            },
        ],
        [t]
    );

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('timeline.breadcrumb_title')} pageTitle={t('timeline.breadcrumb_page_title')} />
                    <Row>
                        <Col lg={12}>
                            <Card className="log-card">
                                <CardHeader className="d-flex align-items-center">
                                    <h5 style={{ color: "#495057" }} className="card-title mb-0 flex-grow-1">{t('timeline.card_title')}</h5>
                                    <div className="flex-shrink-0">
                                        <div className="filter-container hstack gap-2">
                                            <Input
                                                type="select"
                                                className="form-select"
                                                value={actionFilter}
                                                onChange={(e) => setActionFilter(e.target.value)}
                                            >
                                                <option value="all">{t('timeline.filter_all_actions')}</option>
                                                <option value="create">{t('timeline.filter_create_actions')}</option>
                                                <option value="login">{t('timeline.filter_login_actions')}</option>
                                            </Input>
                                            <Input
                                                type="select"
                                                className="form-select"
                                                value={resourceFilter}
                                                onChange={(e) => setResourceFilter(e.target.value)}
                                            >
                                                <option value="all">{t('timeline.filter_all_resources')}</option>
                                                <option value="user">{t('timeline.filter_user')}</option>
                                                <option value="formation">{t('timeline.filter_formation')}</option>
                                                <option value="meeting">{t('timeline.filter_meeting')}</option>
                                                <option value="carriere">{t('timeline.filter_carriere')}</option>
                                                <option value="faq">{t('timeline.filter_faq')}</option>
                                                <option value="ressource">{t('timeline.filter_ressource')}</option>
                                            </Input>
                                            <Input
                                                type="select"
                                                className="form-select"
                                                value={userFilter}
                                                onChange={(e) => setUserFilter(e.target.value)}
                                            >
                                                <option value="all">{t('timeline.filter_all_users')}</option>
                                                {uniqueUsers.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.firstname + ' ' + user.lastname}
                                                    </option>
                                                ))}
                                            </Input>
                                            <Input
                                                type="select"
                                                className="form-select"
                                                value={dateFilter}
                                                onChange={(e) => setDateFilter(e.target.value)}
                                            >
                                                <option value="all">{t('timeline.filter_all_time')}</option>
                                                <option value="today">{t('timeline.filter_today')}</option>
                                                <option value="week">{t('timeline.filter_this_week')}</option>
                                                <option value="month">{t('timeline.filter_this_month')}</option>
                                            </Input>
                                            <Button className="reset-btn" onClick={resetFilters}>
                                                {t('timeline.reset_button')}
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    <div className="horizontal-timeline my-3">
                                        <Swiper
                                            className="timelineSlider"
                                            navigation={true}
                                            slidesPerView={5}
                                            direction="horizontal"
                                            initialSlide={filteredLogs.length - 1}
                                        >
                                            <div className="swiper-wrapper">
                                                {filteredLogs.map(log => (
                                                    <SwiperSlide key={log._id}>
                                                        <div className="card pt-2 border-0 item-box text-center">
                                                            <div className="timeline-content p-3">
                                                                <div>
                                                                    <p className="mb-1 text-muted">{moment(log.timestamp).format('MMMM D, YYYY')}</p>
                                                                    <span className={`badge ${getActionColor(log.action)} text-uppercase mb-2`}>{log.action}</span>
                                                                    <p className="mb-1 font-semibold">{t('timeline.by')}: {log.userId?.firstname + ' ' + log.userId?.lastname || t('timeline.unknown')}</p>
                                                                    <p className="mb-0 text-muted">{t('timeline.role')}: {log.role}</p>
                                                                    <p className="mb-0 text-muted">{moment(log.timestamp).format('hh:mm A')}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </SwiperSlide>
                                                ))}
                                            </div>
                                        </Swiper>
                                    </div>
                                    <h5 className="mt-4">{t('timeline.log_details')}</h5>
                                    <TableContainer
                                        columns={columns}
                                        data={filteredLogs}
                                        isGlobalFilter={false}
                                        customPageSize={8}
                                        className="custom-header-css"
                                        divClass="table-responsive table-card mb-1"
                                        tableClass="log-table min-w-full bg-white align-middle table-nowrap"
                                        theadClass="table-light text-muted"
                                        thClass="py-2 px-4 border"
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Timeline;